/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable eqeqeq */
import React, { useRef, useState, useEffect, useCallback } from "react";
import { VendorList } from "../lib/OptionsAPI";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { CheckIcon, XIcon } from "lucide-react";
import { cn } from "../lib/utils";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { debounce } from "lodash";
import AddEditVendor from "../pages/Vendors/AddEditVendor";
import { ResponsiveDrawerForm } from "./ResponsiveDrawerForm";
import { getModuleNamingBySlug } from "../lib/Misc";
import { useOrganization } from "../lib/context/OrganisationContext";

const VendorSelect = ({
  organizationId,
  trustId,
  frameworkData,
  optionType,
  handleSelect,
  handleBlur,
  className,
  disabled = false,
  initialValue,
  setVendorList,
  setVendorLoading,
  onClear,
  amlctfContact = false,
  loading = false,
}) => {
  const uniqueFrameworks = frameworkData.filter(
    (item, index, self) =>
      index === self.findIndex((t) => t?.value === item?.value)
  );
  const { organizationData } = useOrganization();

  // Separate "new" value item
  const newItem = uniqueFrameworks.find((item) => item.value === "new");
  const restItems = uniqueFrameworks.filter((item) => item.value !== "new");

  // Final list with "new" at the end
  const frameworks = [...restItems, ...(newItem ? [newItem] : [])];
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState(""); // Store search input
  const scrollRef = useRef(null); // Ref for tracking scrolling

  const [showAddVendor, setShowAddVendor] = useState(false);

  const toggleShowAddVendor = () => {
    if (showAddVendor) {
      fetchVendors();
    }
    setShowAddVendor(!showAddVendor);
  };

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const isLoadingMoreRef = useRef(false); // Prevent concurrent calls

  const loadMore = async () => {
    if (!hasMore || isLoadingMoreRef.current) return;

    isLoadingMoreRef.current = true;
    const nextPage = page + 1;

    const moreData = await VendorList(
      organizationData,
      trustId,
      nextPage,
      setVendorLoading,
      setVendorList,
      true,
      amlctfContact,
      organizationId
    );

    setPage(nextPage);
    setHasMore(moreData);

    isLoadingMoreRef.current = false;
  };

  // Manual scroll detection
  const handleScroll = useCallback(
    debounce(() => {
      if (!scrollRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

      if (scrollTop + clientHeight >= scrollHeight - 10) {
        loadMore();
      }
    }, 200), // 200ms debounce
    [page, hasMore]
  );

  const fetchVendors = useCallback(
    debounce(async (query) => {
      setPage(1); // Reset pagination on new search
      setHasMore(true);
      const moreData = await VendorList(
        organizationData,
        trustId,
        1, // Start from page 1 on new search
        setVendorLoading,
        setVendorList,
        true, // Replace list on new search
        amlctfContact,
        organizationId,
        query // Pass the search query
      );

      setHasMore(moreData);
    }, 500), // 500ms delay
    []
  );

  const handleSearch = (e) => {
    const query = e;
    setSearchTerm(query);
    fetchVendors(query);
  };

  const handleClear = (e) => {
    setValue("");
    onClear && onClear("");
  };

  useEffect(() => {
    frameworks?.length == 0 && fetchVendors();
  }, []);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={`w-full justify-between h-9 px-3 ${
              value
                ? "font-normal text-left overflow-hidden"
                : "text-muted-foreground font-normal"
            } ${className || ""}`}
            onBlur={handleBlur}
            disabled={disabled}
            title={
              value
                ? frameworks.find((framework) => framework?.value == value)
                    ?.label
                : ""
            }
          >
            <span className="truncate flex-1 min-w-0 text-left">
              {value
                ? frameworks.find((framework) => framework?.value == value)
                    ?.label
                : `Select ${optionType}...`}
            </span>
            {value && !loading ? (
              <XIcon
                className="ml-2 h-4 w-4 shrink-0 cursor-pointer opacity-50"
                onClick={(event) => {
                  event.stopPropagation();
                  handleClear();
                }}
              />
            ) : (
              <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-full p-0"
          modal={false}
          onPointerDownOutside={(e) => e.stopPropagation()}
          onFocusOutside={(e) => e.preventDefault()}
          onWheelCapture={(e) => e.stopPropagation()}
        >
          <Command>
            <CommandInput
              placeholder={`Search ${optionType}...`}
              className="h-9"
              value={searchTerm}
              onValueChange={handleSearch}
              disablePointerSelection={!trustId}
            />
            <CommandList
              ref={scrollRef}
              onScroll={handleScroll} // Attach scroll event listener
              className="max-h-60 overflow-y-auto"
            >
              <CommandEmpty>No {optionType} found.</CommandEmpty>
              <CommandGroup>
                {frameworks.map((framework) => (
                  <CommandItem
                    key={framework?.value}
                    value={framework?.label}
                    onSelect={(currentValue) => {
                      setOpen(false);
                      if (
                        currentValue ===
                        `Add ${getModuleNamingBySlug(
                          organizationData?.organizationData,
                          "vendors",
                          "Vendor"
                        )}`
                      ) {
                        setShowAddVendor(true);
                      } else {
                        handleSelect(
                          frameworks.find(
                            (framework) => framework?.label === currentValue
                          )?.value
                        );
                        setValue(
                          frameworks.find(
                            (framework) => framework?.label === currentValue
                          )?.value
                        );
                      }
                    }}
                    disabled={disabled}
                    className={value === framework?.value ? "optionActive" : ""}
                  >
                    {framework?.label}
                    <CheckIcon
                      className={cn(
                        "ml-auto h-4 w-4 optionChecked",
                        value === framework?.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <ResponsiveDrawerForm
        description={""}
        title={`Add ${getModuleNamingBySlug(
          organizationData?.organizationData,
          "vendors",
          "Vendor"
        )}`}
        handleOpen={() => {}}
        handleClose={() => {
          setShowAddVendor(false);
        }}
        buttonText={""}
        className={""}
        open={showAddVendor}
        buttonClassName={""}
        setOpen={setShowAddVendor}
        write={true}
      >
        <AddEditVendor
          edit={false}
          toggleShowAddEditVendor={toggleShowAddVendor}
        />
      </ResponsiveDrawerForm>
    </>
  );
};

export default VendorSelect;
